import { Currency } from '../common/models/enums/currency.enum';

export class MoneyAmount {
  private constructor(private readonly minUnits: number) {}

  static fromMinUnits(amount: number): MoneyAmount {
    if (amount < 0) {
      throw new Error('Amount cannot be negative');
    }
    if (!Number.isInteger(amount)) {
      throw new Error('Minimal units must be integers');
    }

    return new MoneyAmount(amount);
  }

  static fromMainUnits(amount: number): MoneyAmount {
    if (amount < 0) {
      throw new Error('Amount cannot be negative');
    }
    if (!Number.isFinite(amount)) {
      throw new Error('Amount must be a finite number');
    }
    return new MoneyAmount(Math.round(amount * 100));
  }

  toMainUnits(): number {
    return this.minUnits / 100;
  }

  toMinUnits(): number {
    return this.minUnits;
  }

  isZero(): boolean {
    return this.minUnits === 0;
  }

  isPositive(): boolean {
    return this.minUnits > 0;
  }

  isNegative(): boolean {
    return this.minUnits < 0;
  }

  add(other: MoneyAmount): MoneyAmount {
    return MoneyAmount.fromMinUnits(this.minUnits + other.toMinUnits());
  }

  subtract(other: MoneyAmount): MoneyAmount {
    return MoneyAmount.fromMinUnits(this.minUnits - other.toMinUnits());
  }

  format(currency: Currency): string {
    return `${this.toMainUnits().toFixed(2)} ${currency}`;
  }

  toFormattedAmount(): string {
    return this.toMainUnits().toFixed(2);
  }

  toString(): never {
    throw new Error(
      'MoneyAmount should not be converted to string implicitly. Use toFormattedAmount() or format() instead.',
    );
  }

  valueOf(): never {
    throw new Error(
      'MoneyAmount should not be converted to primitive implicitly. Use format(currency) or toFormattedAmount() instead.',
    );
  }
}
